import type { BrandId } from '@nadoutong/contracts';
import { BRAND_IDS, normalizeBrandId } from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import { useCommissionLeaderboard } from '../api/commissionReports.ts';
import { OpsApiError } from '../api/client.ts';
import { ROLE_OPS_ADMIN, ROLE_OPS_FINANCE_MANAGER, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { formatCnyCents, formatUsdMicros, recentPeriodKeys } from '../components/formatters.ts';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Chip,
  EmptyState,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * /crm/leaderboard — per-manager brand leaderboard (epic #1034 E5-M5,
 * #1147). Its own page, gated by COMMISSION_LEADERBOARD_ROLES (admin +
 * finance-manager + sales-lead), so FINANCE reaches the cross-brand
 * standings + house line WITHOUT the procurement-bearing lead pricing page
 * (which stays admin+lead). NOT customer-manager reachable — a leaderboard
 * is a cross-book view.
 *
 * ATTRIBUTION LAYERING: the commission column reads statements (RECEIPT
 * attribution) and the book-margin column reads lot-owner consumption (LOT
 * attribution). They sit alongside — never summed — and are labeled apart.
 * Scope is server-derived: lead → own brand (pinned); admin/finance →
 * cross-brand (optional single-brand filter) + house line.
 */

const ALL_BRANDS = '__all__';

function errorText(error: unknown): string {
  if (error instanceof OpsApiError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : String(error);
}

export function CommissionLeaderboardPage(): JSX.Element {
  const t = useT();
  const { user, status } = useSession();
  const crossBrand = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN, ROLE_OPS_FINANCE_MANAGER]);
  const periods = useMemo(() => recentPeriodKeys(6), []);
  const [period, setPeriod] = useState<string>(() => periods[0] ?? '');
  // admin/finance may filter to one brand or view all (null = cross-brand);
  // a lead sends null and ops-api pins their profile brand.
  const [selectedBrand, setSelectedBrand] = useState<BrandId | null>(null);
  const brand = crossBrand ? selectedBrand : null;

  const query = useCommissionLeaderboard({ brand, period }, status === 'authenticated');
  const rows = query.data?.rows ?? [];

  return (
    <section className="flex flex-col gap-8" data-testid="commission-leaderboard-page">
      <PageHeader
        title={t('lead_leaderboard.title')}
        sub={t('lead_leaderboard.sub')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {crossBrand ? (
              <Select
                value={selectedBrand ?? ALL_BRANDS}
                onValueChange={(next) =>
                  setSelectedBrand(next === ALL_BRANDS ? null : normalizeBrandId(next))
                }
                options={[
                  { value: ALL_BRANDS, label: t('lead_leaderboard.all_brands') },
                  ...BRAND_IDS.map((id) => ({ value: id, label: id })),
                ]}
                ariaLabel={t('lead_leaderboard.brand_aria')}
                mono
                testId="commission-leaderboard-brand-select"
              />
            ) : (
              <Chip tone="signal" testId="commission-leaderboard-brand-chip">
                {query.data?.brand ?? '…'}
              </Chip>
            )}
            <Select
              value={period}
              onValueChange={setPeriod}
              options={periods.map((p) => ({ value: p, label: p }))}
              ariaLabel={t('lead_leaderboard.period_aria')}
              mono
              testId="commission-leaderboard-period-select"
            />
          </div>
        }
      />

      <Panel
        caption={
          <span className="flex items-baseline gap-2">
            <span>{t('lead_leaderboard.title')}</span>
            <span className="text-dense font-normal text-paper-low">
              {t('lead_leaderboard.attribution')}
            </span>
          </span>
        }
        count={rows.length}
        testId="commission-leaderboard"
      >
        {query.isLoading ? <TableSkeleton rows={4} /> : null}
        {query.isError ? (
          <EmptyState
            title={t('lead_leaderboard.error')}
            hint={errorText(query.error)}
            testId="commission-leaderboard-error"
          />
        ) : null}
        {query.isSuccess ? (
          <>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('lead_leaderboard.table.manager')}</Th>
                  <Th num>{t('lead_leaderboard.table.commission')}</Th>
                  <Th num>{t('lead_leaderboard.table.book_margin')}</Th>
                  <Th num>{t('lead_leaderboard.table.customers')}</Th>
                </Tr>
              </THead>
              <TBody>
                {rows.length === 0 ? (
                  <TableEmpty colSpan={4} testId="commission-leaderboard-empty">
                    {t('lead_leaderboard.empty')}
                  </TableEmpty>
                ) : (
                  rows.map((row) => (
                    <Tr
                      key={`${row.owner_ops_user_id}:${row.brand}`}
                      data-testid={`commission-leaderboard-${row.owner_ops_user_id}`}
                    >
                      <Td mono emphasis>
                        {row.is_house ? t('lead_leaderboard.house') : <OpsUserRef id={row.owner_ops_user_id} />}
                      </Td>
                      <Td num>
                        {formatUsdMicros(
                          row.commission_displayed_total_usd_micros,
                          row.commission_displayed_total_usd_micros < 0,
                        )}
                      </Td>
                      <Td num>
                        {formatCnyCents(row.book_margin_cny_cents, row.book_margin_cny_cents < 0)}
                      </Td>
                      <Td num>{row.customer_count}</Td>
                    </Tr>
                  ))
                )}
              </TBody>
            </Table>
            {/* Lower-bound caveat: book margin is lot-attributed consumption
                and lags when a charge's allocation is still pending replay
                (#1147). Commission is receipt-based and unaffected. */}
            <p
              className="mt-2 text-micro text-paper-low"
              data-testid="commission-leaderboard-lag-caveat"
            >
              {t('lead_leaderboard.lag_caveat')}
            </p>
          </>
        ) : null}
      </Panel>
    </section>
  );
}
